import { useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { ROUTES, PERMISSIONS } from '@/constants';
import { useAuthContext } from '@/context/AuthContext';
import { filterNavItems } from '@/utils/navAccess';
import { cn } from '@/lib/utils';
import {
    APP_SIDEBAR_ASIDE_CLASS,
    APP_SIDEBAR_HEADER_ROW_CLASS,
    APP_SIDEBAR_TOGGLE_BUTTON_CLASS,
    appSidebarNavClassName,
    APP_SIDEBAR_SECTION_LABEL_CLASS,
    appSidebarNavLinkClassName,
} from '@/lib/sidebarNav';
import {
    LayoutDashboard,
    Users,
    FolderKanban,
    Shield,
    MapPin,
    ChevronRight,
    Building2,
    Bell,
    UserCog,
    BookOpen,
    FileJson2,
    Upload,
    KeyRound,
    Layers,
    ClipboardList,
    ListChecks,
    FileDown,
    Network,
    ChevronsUp,
    Hourglass,
    Gauge,
    ClipboardCheck,
    MonitorCog,
} from 'lucide-react';

import { TripleChevronCloseIcon, TripleChevronOpenIcon } from '@/components/layout/PortalSidebarToggle';
import { useIsMobile } from '@/hooks/useIsMobile';
import { ExecutiveAppHeader } from '@/components/layout/ExecutiveAppHeader';
import { DashboardHeaderSlotProvider, DashboardHeaderSlot } from '@/context/DashboardHeaderSlotContext';
import { ChatProvider } from '@/features/chat/context/ChatContext';
import { AiAssistantProvider } from '@/features/aiAssistant/context/AiAssistantContext';
import { AiAssistantNavItem } from "@/features/aiAssistant/components/AiAssistantNavItem";

/**
 * Same shell as TaskForceChairLayout: ExecutiveAppHeader + collapsible icon rail / expanded nav + main p-3.
 * Nav links are admin-specific; dashboard content is ExecutiveDashboardPage (re-exported from AdminDashboardPage).
 */
export function AdminLayout() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const isMobile = useIsMobile();
    const { user } = useAuthContext();
    const location = useLocation();

    const importSubNav = useMemo(
        () => [
            {
                label: 'Users',
                to: ROUTES.ADMIN_IMPORT_USERS,
                icon: Users,
                requiredPermissions: [PERMISSIONS.MANAGE_USER],
            },
            {
                label: 'Organizations',
                to: ROUTES.ADMIN_IMPORT_ORGANIZATIONS,
                icon: Building2,
                requiredPermissions: [PERMISSIONS.MANAGE_USER],
            },
            {
                label: 'Tasks',
                to: ROUTES.ADMIN_IMPORT_TASKS,
                icon: FileJson2,
                requiredPermissions: [PERMISSIONS.MANAGE_USER],
            },
        ],
        [],
    );

    const visibleImportSub = useMemo(
        () => filterNavItems(user, importSubNav),
        [user, importSubNav],
    );
    const importSectionActive = location.pathname.startsWith('/admin/import');

    const tabs = useMemo(
        () => [
            {
                label: 'Dashboard',
                to: ROUTES.ADMIN_DASHBOARD,
                end: true,
                icon: LayoutDashboard,
                requiredPermissions: [PERMISSIONS.VIEW_TASK_FORCE],
            },

            // ── Delivery hierarchy: task force → pillar → project → task ──
            {
                label: 'Task Force Management',
                to: ROUTES.ADMIN_TASK_FORCES,
                icon: FolderKanban,
                requiredPermissions: [PERMISSIONS.VIEW_TASK_FORCE],
            },
            {
                label: 'Pillar Management',
                to: ROUTES.ADMIN_PILLARS,
                icon: Layers,
                requiredPermissions: [PERMISSIONS.VIEW_PILLAR],
            },
            {
                label: 'Project Management',
                to: ROUTES.ADMIN_PROJECTS,
                icon: ClipboardList,
                requiredPermissions: [PERMISSIONS.VIEW_PROJECT],
            },
            {
                label: 'Task Management',
                to: ROUTES.ADMIN_TASKS,
                icon: ListChecks,
                requiredPermissions: [PERMISSIONS.VIEW_TASK],
            },

            // ── Directory roles, most senior first ──
            {
                label: 'Administrator Management',
                to: ROUTES.ADMIN_ADMINISTRATORS,
                icon: Shield,
                requiredPermissions: [PERMISSIONS.MANAGE_USER],
            },
            {
                label: 'Executive Management',
                to: ROUTES.ADMIN_EXECUTIVES,
                icon: UserCog,
                requiredPermissions: [PERMISSIONS.VIEW_TASK_FORCE],
            },
            {
                label: 'Task Force Chair Management',
                to: ROUTES.ADMIN_TASK_FORCE_CHAIRS,
                icon: ChevronRight,
                requiredPermissions: [PERMISSIONS.VIEW_TASK_FORCE],
            },
            {
                label: 'Pillar Chair Management',
                to: ROUTES.ADMIN_PILLAR_CHAIRS,
                icon: Users,
                requiredPermissions: [PERMISSIONS.MANAGE_USER],
            },
            {
                label: 'Project Manager Management',
                to: ROUTES.ADMIN_PROJECT_MANAGERS,
                icon: Users,
                requiredPermissions: [PERMISSIONS.MANAGE_USER],
            },
            {
                label: 'Member Management',
                to: ROUTES.ADMIN_MEMBERS,
                icon: Users,
                requiredPermissions: [PERMISSIONS.MANAGE_USER],
            },

            // ── Accounts, access and platform settings ──
            {
                label: 'User Management',
                to: ROUTES.ADMIN_USERS,
                icon: Users,
                requiredPermissions: [PERMISSIONS.MANAGE_USER],
            },
            {
                label: 'Account Management',
                to: ROUTES.ADMIN_ACCOUNTS,
                icon: KeyRound,
                requiredPermissions: [PERMISSIONS.MANAGE_USER],
            },
            {
                label: 'Role Management',
                to: ROUTES.ADMIN_ROLES,
                icon: Shield,
                requiredPermissions: [PERMISSIONS.MANAGE_USER],
            },
            {
                label: 'Notification Management',
                to: ROUTES.ADMIN_NOTIFICATION_MANAGEMENT,
                icon: Bell,
                requiredPermissions: [PERMISSIONS.MANAGE_ROLE],
            },
            {
                label: 'Organization Management',
                to: ROUTES.ADMIN_ORGANIZATIONS,
                icon: Building2,
                requiredPermissions: [PERMISSIONS.MANAGE_USER],
            },
            {
                label: 'City Management',
                to: ROUTES.ADMIN_CITIES,
                icon: MapPin,
                requiredPermissions: [PERMISSIONS.MANAGE_USER],
            },
            {
                label: 'Auto Escalation',
                to: ROUTES.ADMIN_AUTO_ESCALATION,
                icon: ChevronsUp,
                requiredPermissions: [PERMISSIONS.MANAGE_ESCALATION],
            },
            {
                label: 'Countdown Management',
                to: ROUTES.ADMIN_COUNTDOWN,
                icon: Hourglass,
                requiredPermissions: [PERMISSIONS.MANAGE_USER],
            },
            {
                label: 'Operational Indicators',
                to: ROUTES.ADMIN_OPERATIONAL_INDICATORS,
                icon: Gauge,
                requiredPermissions: [PERMISSIONS.MANAGE_USER],
            },
            {
                label: 'Registration Confirmation',
                to: ROUTES.ADMIN_REGISTRATION_CONFIRMATION,
                icon: ClipboardCheck,
                requiredPermissions: [PERMISSIONS.MANAGE_USER],
            },
            {
                label: 'Operational Dashboard Viewers',
                to: ROUTES.ADMIN_OPERATIONAL_DASHBOARD_VIEWERS,
                icon: MonitorCog,
                requiredPermissions: [PERMISSIONS.MANAGE_USER],
            },
            {
                label: 'Structure',
                to: ROUTES.ADMIN_STRUCTURE,
                icon: Network,
                // No permission or role gate: every account may see the structure.
                // Where the tree is rooted per role is decided by the server.
            },
            {
                label: 'Export',
                to: ROUTES.ADMIN_EXPORT,
                icon: FileDown,
                // No permission or role gate: every account may export. What differs
                // per role is the scope, which the server derives from the account.
            },
            {
                label: 'User Manual',
                to: ROUTES.ADMIN_HELP,
                icon: BookOpen,
                requiredPermissions: [PERMISSIONS.VIEW_TASK_FORCE],
            },
        ],
        [],
    );

    const visibleTabs = useMemo(() => filterNavItems(user, tabs), [user, tabs]);

    return (
        <DashboardHeaderSlotProvider>
            <ChatProvider>
                <AiAssistantProvider>
            <div className="flex h-screen h-dvh flex-col gap-3 overflow-hidden bg-background p-3">
                <ExecutiveAppHeader
                    title="Admin Dashboard"
                    subtitle="Readiness Platform"
                    center={<DashboardHeaderSlot />}
                    showChatButton={false}
                    showNotifications={false}
                    onOpenSidebar={() => setSidebarOpen(true)}
                />

                <div className="relative flex min-h-0 flex-1 overflow-hidden">
                    {isMobile && sidebarOpen ? (
                        <div
                            className="fixed inset-0 z-30 bg-black/40 md:hidden"
                            onClick={() => setSidebarOpen(false)}
                            aria-hidden
                        />
                    ) : null}
                    <aside
                        className={cn(
                            APP_SIDEBAR_ASIDE_CLASS,
                            isMobile || sidebarOpen ? 'w-64' : 'w-14',
                            isMobile && !sidebarOpen ? '-translate-x-full' : 'translate-x-0',
                        )}
                    >
                        <div className={APP_SIDEBAR_HEADER_ROW_CLASS}>
                            {sidebarOpen ? (
                                <div className="flex min-w-[16rem] w-full justify-end px-2 py-2">
                                    <button
                                        type="button"
                                        onClick={() => setSidebarOpen(false)}
                                        className={APP_SIDEBAR_TOGGLE_BUTTON_CLASS}
                                        aria-label="Collapse sidebar to icons only"
                                        title="Show icons only"
                                    >
                                        <TripleChevronCloseIcon />
                                    </button>
                                </div>
                            ) : (
                                <div className="flex w-full justify-center py-2">
                                    <button
                                        type="button"
                                        onClick={() => setSidebarOpen(true)}
                                        className={APP_SIDEBAR_TOGGLE_BUTTON_CLASS}
                                        aria-label="Expand sidebar"
                                        title="Expand sidebar"
                                    >
                                        <TripleChevronOpenIcon />
                                    </button>
                                </div>
                            )}
                        </div>
                        <nav className={appSidebarNavClassName(sidebarOpen)}>
                            {sidebarOpen ? (
                                <p className={APP_SIDEBAR_SECTION_LABEL_CLASS}>
                                    Navigation
                                </p>
                            ) : null}
                            {visibleTabs.map(({ requiredPermissions: _rp, requiredRole: _rr, ...tab }) => (
                                <NavLink
                                    key={tab.to}
                                    to={tab.to}
                                    end={tab.end}
                                    title={tab.label}
                                    onClick={() => isMobile && setSidebarOpen(false)}
                                    className={({ isActive }) =>
                                        appSidebarNavLinkClassName({ isActive, expanded: sidebarOpen })
                                    }
                                >
                                    <tab.icon className="h-5 w-5 flex-shrink-0" aria-hidden />
                                    <span className={cn('leading-tight', !sidebarOpen && 'sr-only')}>{tab.label}</span>
                                </NavLink>
                            ))}

                            {visibleImportSub.length > 0 ? (
                                <>
                                    {sidebarOpen ? (
                                        <p className={cn(APP_SIDEBAR_SECTION_LABEL_CLASS, 'mt-3')}>Import</p>
                                    ) : null}
                                    {sidebarOpen ? (
                                        visibleImportSub.map(
                                            ({ requiredPermissions: _rp, requiredRole: _rr, ...tab }) => (
                                                <NavLink
                                                    key={tab.to}
                                                    to={tab.to}
                                                    title={tab.label}
                                                    onClick={() => isMobile && setSidebarOpen(false)}
                                                    className={({ isActive }) =>
                                                        appSidebarNavLinkClassName({
                                                            isActive,
                                                            expanded: sidebarOpen,
                                                        })
                                                    }
                                                >
                                                    <tab.icon className="h-5 w-5 flex-shrink-0" aria-hidden />
                                                    <span
                                                        className={cn(
                                                            'leading-tight pl-0.5',
                                                            !sidebarOpen && 'sr-only',
                                                        )}
                                                    >
                                                        {tab.label}
                                                    </span>
                                                </NavLink>
                                            ),
                                        )
                                    ) : (
                                        <NavLink
                                            to={ROUTES.ADMIN_IMPORT}
                                            end
                                            title="Import"
                                            onClick={() => isMobile && setSidebarOpen(false)}
                                            className={({ isActive }) =>
                                                appSidebarNavLinkClassName({
                                                    isActive: isActive || importSectionActive,
                                                    expanded: false,
                                                })
                                            }
                                        >
                                            <Upload className="h-5 w-5 flex-shrink-0" aria-hidden />
                                            <span className="sr-only">Import</span>
                                        </NavLink>
                                    )}
                                </>
                            ) : null}
                            <AiAssistantNavItem
                                expanded={sidebarOpen}
                                onActivate={() => isMobile && setSidebarOpen(false)}
                            />
                        </nav>
                    </aside>

                    <main className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-0.5 md:p-3">
                        <Outlet />
                    </main>
                </div>
            </div>
                </AiAssistantProvider>
            </ChatProvider>
        </DashboardHeaderSlotProvider>
    );
}

export default AdminLayout;
